import {useModal} from '@hooks';
import {trackEvent} from '@lib/infrastructure';
import {getAllConnectedUsers, useAppSelector} from '@lib/store';
import {LocalDateString, OfficeWorkday, WorkspaceReservation} from '@lib/store/src/data/timeslots/apiTypes';
import {ModalCell, UserCell} from '@molecules';
import {FlexRow, FlexCol, H4, P, Div} from '@quarks';
import {format} from 'date-fns';
import {useState} from 'react';
import {useTranslation} from 'react-i18next';
import {StyledModalPage} from '../../BookWorkspaceCard/styles';
import {Footer} from './Footer';
import {MapPreviewCard} from './MapPreviewCard';
import {useReservationDetails} from './useReservationDetails';
import {MemberCell} from './MemberCell';
import {FullMapView} from './FullMapView';
import {IconNames} from 'mapiq-atoms';
import {useConnectionsSchedule, constructConnectionsOverview} from './useConnectionsSchedule';

// Work in progress
export const BookingDetailsCard_timeslots = ({
  date: dateKey,
  workday,
  workspaceReservation,
}: {
  date: LocalDateString;
  workday: OfficeWorkday;
  workspaceReservation: WorkspaceReservation;
}) => {
  const [inMapView, setInMapView] = useState(false);

  const {t} = useTranslation();
  const {closeModal} = useModal();
  const reservationsGroup = useReservationDetails(workspaceReservation);

  const [isSaving, setIsSaving] = useState(false);

  const isInvitation = workspaceReservation.invitationTo !== null;
  const isGroupBooking = workspaceReservation.hasInvitations;

  const modalTitle = isInvitation
    ? t('screen:WorkspaceInvitationDetails')
    : isGroupBooking
    ? t('workplace:GroupBooking')
    : t('screen:DeskDetail');

  const workspace = workspaceReservation.workspace;
  const title =
    workspace.nodeType === 'Desk' || workspace.nodeType === 'Area'
      ? workspace.areaName
      : workspace.nodeType === 'Floor'
      ? workspace.floorName
      : workspace.buildingName;

  const subtitle =
    workspace.nodeType === 'Building'
      ? ''
      : workspace.nodeType === 'Floor'
      ? workspace.buildingName
      : `${workspace.buildingName}, ${workspace.floorName}`;
  const icon: IconNames = workspace.nodeType === 'Desk' ? 'desk' : workspace.nodeType === 'Area' ? 'area' : 'floor';

  const connectionOverviewTitle = isGroupBooking
    ? t('connection:OtherConnectionsWorkingInBuilding', {
        building: workspace.buildingName,
      })
    : t('connection:ConnectionsWorkingInBuilding', {
        building: workspace.buildingName,
      });
  const connections = useConnectionsSchedule(dateKey);
  const connectedUsers = useAppSelector(getAllConnectedUsers);

  const overview = constructConnectionsOverview(
    connectedUsers,
    connections.workspaceResevations,
    workspaceReservation.workspace,
  );

  return (
    <StyledModalPage
      title={modalTitle}
      onBack={inMapView ? () => setInMapView(false) : undefined}
      onClose={() => {
        closeModal();
        trackEvent('CalendarOverview_BookingDetails__Close', {
          date: dateKey,
          isGroupBooking,
        });
      }}
      bottom={
        <Footer
          workday={workday}
          workspaceReservation={workspaceReservation}
          isSaving={isSaving}
          setIsSaving={setIsSaving}
        />
      }>
      {inMapView && reservationsGroup.status === 'fulfilled' && (
        <FullMapView
          title={title}
          subtitle={subtitle}
          icon={icon}
          members={reservationsGroup.data}
        />
      )}

      {!inMapView && (
        <>
          <ModalCell>
            <MapPreviewCard
              workspaceReservation={workspaceReservation}
              members={reservationsGroup.status === 'fulfilled' ? reservationsGroup.data : []}
              onClick={() => setInMapView(true)}
            />
          </ModalCell>

          <ModalCell>
            <FlexRow alignItems="flex-start">
              <FlexCol gap={4}>
                <H4 as="h2">{title}</H4>
                <P>{subtitle}</P>
                <P>{format(new Date(dateKey), 'EEEE dd MMMM')}</P>
              </FlexCol>

              <code>TODO: Capacity indicator</code>
              {/* <CapacityIndicator
                      capacity={100}
                      occupation={10}
                    /> */}
            </FlexRow>
          </ModalCell>

          {(isInvitation || isGroupBooking) && (
            <ModalCell>
              <h2>{t('Invitees')}</h2>
              {reservationsGroup.status === 'fulfilled' ? (
                reservationsGroup.data.map((member, i) => (
                  <MemberCell
                    member={member}
                    key={i}
                  />
                ))
              ) : (
                <h2>LOADING</h2>
              )}
            </ModalCell>
          )}

          {/* TODO: booked for you overview */}

          {overview.length > 0 && (
            <ModalCell>
              <H4 as="h2">{connectionOverviewTitle}</H4>

              {overview.map((o, i) => {
                return (
                  <Div key={i}>
                    <P
                      fontSize="sm"
                      fontWeight="bold">
                      {o.isUserWorkspace ? t('connection:YourLocation') : o.locationName}
                    </P>
                    {o.occupants.map((occ) => (
                      <UserCell
                        separator={false}
                        padding="small"
                        key={occ.user.id}
                        user={occ.user}
                      />
                    ))}
                  </Div>
                );
              })}
            </ModalCell>
          )}
        </>
      )}
    </StyledModalPage>
  );
};
